import { useTranslation } from 'react-i18next';
import config from '../../../../../app/config';
import { useDispatch } from 'react-redux';
import React, { useEffect } from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import * as _debounce from 'lodash/debounce';
import { hasPermission, Permissions } from '../../../../../store/reducers/user';
import div from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import PropTypes from 'prop-types';
import { emitChangedWait, getObsScore, obsStatuses, obsTypes } from '../obsUtils';
import renderAcceptSection from './renderAcceptSection';
import ObsNumericStepper from './obsNumericStepper';
import AgyleBlockSwitch from '../../../../../components/ui/switch/agyleBlockSwitch';
import { useTheme } from '@mui/material/styles';
import { obsStyles, obsStylesMews } from '../obsStyle';

function ObsSpo2(props) {
    const classes = obsStyles();
    const classesMews = obsStylesMews();
    const { t } = useTranslation();
    const theme = useTheme();

    const { type, airType, ageRangeYears } = props;
    const allowDefaultValue = config?.observations?.defaultValues;
    const disableAcceptButtons = config?.observations?.disableAcceptButtons;

    const dispatch = useDispatch();
    const [status, setStatus] = React.useState(props.status || obsStatuses.new);
    const [hypercapnic, setHypercapnic] = React.useState(props.hypercapnic);
    const [spo2scale, setSpo2scale] = React.useState(allowDefaultValue ? props.spo2scale : null);
    const [score, setScore] = React.useState(props.score);
    const [accepted, setAccepted] = React.useState(props.accepted);
    const [acceptDate, setAcceptDate] = React.useState(props.acceptDate);

    function setDefaults() {
        setSpo2scale(allowDefaultValue ? 100 : null);
        setHypercapnic(false);
    }

    // For Mobile view
    useEffect(() => {
        if (props.ipodView && props.status !== obsStatuses.new) {
            setSpo2scale(props.spo2scale);
        }
    }, []);

    useEffect(() => {
        if ((_.isUndefined(spo2scale) || _.isNaN(spo2scale)) && allowDefaultValue) {
            if (_.isUndefined(props.spo2scale) || props.spo2scale == null || _.isNaN(props.spo2scale)) {
                setSpo2scale(100);
            } else {
                setSpo2scale(props.spo2scale);
            }
        }
    }, [props.spo2scale]);

    useEffect(() => {
        if (type === obsTypes.news && !hypercapnic) {
            if (_.isUndefined(props.hypercapnic)) {
                setHypercapnic(false);
            } else {
                setHypercapnic(props.hypercapnic);
            }
        }
    }, [props.hypercapnic]);

    useEffect(() => {
        const checkValue =
            (!_.isNull(spo2scale) && status === obsStatuses.new) || (status !== obsStatuses.completed && status !== obsStatuses.new);

        if (disableAcceptButtons && checkValue) {
            acceptObs();
        }
    }, [spo2scale, hypercapnic, status]);

    const clearAccepted = () => {
        setStatus(obsStatuses.new);
        setAccepted(false);
        setAcceptDate(null);
    };

    const resetObs = () => {
        clearAccepted();
        setDefaults();
    };

    const acceptObs = () => {
        if (status === obsStatuses.new) {
            setStatus(obsStatuses.completed);
        }
        setAcceptDate(moment());
        setAccepted(true);
    };

    const lazyEmitOnChange = React.useMemo(() => {
        return _debounce((value, onChange) => {
            // console.log("lazyEmitOnChange", value);
            onChange(value);
        }, emitChangedWait);
    }, []);

    const emitChanged = () => {
        if (props.onChange) {
            // console.log("emitChanged");
            lazyEmitOnChange(
                {
                    status,
                    hypercapnic,
                    spo2scale,
                    score,
                    accepted,
                    acceptDate,
                },
                props.onChange,
            );
        }
    };
    useEffect(() => {
        emitChanged();
    }, [status, spo2scale, score, accepted, acceptDate]);

    const updateScore = () => {
        let newScore = 0;
        if (status === obsStatuses.notRecorded || status === obsStatuses.patientRefused || status === obsStatuses.equipment) {
            newScore = 0;
        } else if (status === obsStatuses.patientCondition) {
            newScore = 3;
        } else {
            if (type === obsTypes.news) {
                if (hypercapnic) {
                    if (!spo2scale) {
                        newScore = spo2scale === 0 ? 3 : 0;
                    } else if (spo2scale <= 83) {
                        newScore = 3;
                    } else if (spo2scale > 83 && spo2scale < 86) {
                        newScore = 2;
                    } else if (spo2scale >= 86 && spo2scale < 88) {
                        newScore = 1;
                    } else {
                        if (airType === 'oxygen') {
                            if (!spo2scale) {
                                newScore = spo2scale === 0 ? 3 : 0;
                            } else if (spo2scale >= 88 && spo2scale < 93) {
                                newScore = 0;
                            } else if (spo2scale >= 93 && spo2scale < 95) {
                                newScore = 1;
                            } else if (spo2scale >= 95 && spo2scale <= 96) {
                                newScore = 2;
                            } else {
                                newScore = 3;
                            }
                        } else {
                            newScore = 0;
                        }
                    }
                } else {
                    newScore = getObsScore('spo2', type, ageRangeYears, spo2scale);
                }
            } else if (type === obsTypes.pews || type === obsTypes.mews) {
                newScore = getObsScore('spo2', type, ageRangeYears, spo2scale);
            }
        }
        setScore(newScore);
    };

    useEffect(() => {
        updateScore();
    }, [status, hypercapnic, spo2scale, airType]);

    const onHypercapnicChange = (val) => {
        if (dispatch(hasPermission(Permissions.AGYLE_CAN_SET_HYPERCAPNIC_RESPIRATORY_FAILURE))) {
            setHypercapnic(val);
            clearAccepted();
        }
    };

    const onSpo2scaleChange = (value) => {
        setSpo2scale(value);
        clearAccepted();
    };

    const onStatusChange = (value) => {
        clearAccepted();
        if (value && value !== status) {
            setStatus(value);
        }
    };

    return (
        <div className={classes.obsRoot}>
            <div className={classes.obsContent}>
                <Grid container alignItems={'center'} spacing={2}>
                    {type === obsTypes.news && (
                        <Grid item xs={12}>
                            <AgyleBlockSwitch
                                onChange={onHypercapnicChange}
                                value={hypercapnic}
                                disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_SET_HYPERCAPNIC_RESPIRATORY_FAILURE))}
                                customLabels={{
                                    yes: t('Scale 1'),
                                    no: t('Scale 2'),
                                }}
                                valueObject={{
                                    true: false,
                                    false: true,
                                }}
                                style={{
                                    backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
                                }}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <ObsNumericStepper
                            value={spo2scale}
                            minValue={0}
                            maxValue={100}
                            // label={t('Sp0₂%')}
                            label={hypercapnic ? t('Sp0₂% Scale 2 Oxygen escalation (%)') : t('Sp0₂% Scale 1 Oxygen escalation (%)')}
                            onChange={onSpo2scaleChange}
                        />
                    </Grid>

                    {renderAcceptSection({
                        classes: type === obsTypes.mews ? classesMews : classes,
                        t,
                        hideScore: type === obsTypes.neuro,
                        score,
                        status,
                        onStatusChange,
                        resetObs,
                        accepted,
                        acceptObs,
                        acceptDate,
                        acceptDisabled: _.isNull(spo2scale),
                        showStatusNR: type === obsTypes.pews,
                        hideStatuses: type === obsTypes.neuro,
                        disableAcceptButtons,
                    })}
                </Grid>
            </div>
        </div>
    );
}

ObsSpo2.propTypes = {
    type: PropTypes.oneOf([obsTypes.news, obsTypes.pews, obsTypes.neuro, obsTypes.mews]),
    ageRangeYears: PropTypes.string,
    status: PropTypes.string,
    score: PropTypes.number,
    airType: PropTypes.oneOf(['room', 'oxygen']),
    hypercapnic: PropTypes.bool,
    spo2scale: PropTypes.number,
    accepted: PropTypes.bool,
    acceptDate: PropTypes.instanceOf(moment),
    onChange: PropTypes.func,
};
ObsSpo2.defaultProps = {
    type: obsTypes.news,
    status: obsStatuses.new,
    score: 0,
    hypercapnic: false,
    accepted: false,
    acceptDate: null,
};

export default ObsSpo2;
